import { readNullableOneOf, readNullableString, readOneOf, readRecord, readString, readStringList } from '@/lib/readers';
import type { LiveRow } from '@/lib/pendingWrites';

/** The types of file a brand has. Each file is one document below the brand. */
export const BRAND_ASSET_TYPES = [
  'logo',
  'visual_element',
  'audio',
  'file',
  'brand_guideline',
] as const;
export type BrandAssetType = (typeof BRAND_ASSET_TYPES)[number];

export const AUDIO_KINDS = ['music', 'voice_over'] as const;
export type AudioKind = (typeof AUDIO_KINDS)[number];

export const VISUAL_ELEMENT_KINDS = ['start_card', 'end_card', 'other'] as const;
export type VisualElementKind = (typeof VISUAL_ELEMENT_KINDS)[number];

/** What the transcoder-service made of an uploaded video. Absent until one job finishes. */
export const TRANSCODE_STATUSES = ['completed', 'failed'] as const;
export type TranscodeStatus = (typeof TRANSCODE_STATUSES)[number];

export interface BrandAsset extends LiveRow {
  id: string;
  brand: string;
  assetType: BrandAssetType;
  name: string;
  fileObject: string;
  kind: AudioKind | VisualElementKind | null;
  /** The transcoded MP4 a browser can play. Null until the transcoder reports one. */
  playbackObject: string | null;
  /** The smaller copy of the video, which the video editor uses. */
  proxyObject: string | null;
  transcodeStatus: TranscodeStatus | null;
  isAiCreated: boolean;
  waiverGivenBy: string | null;
  tags: string[];
  /** ISO timestamp from the document's metadata; empty when the document has none. */
  createdAt: string;
}

/** The kind each asset type reads, or null for a type without kinds. */
const KIND_OF: Record<BrandAssetType, (value: unknown) => BrandAsset['kind']> = {
  audio: (value) => readOneOf(AUDIO_KINDS, value, 'music'),
  visual_element: (value) => readOneOf(VISUAL_ELEMENT_KINDS, value, 'other'),
  logo: () => null,
  file: () => null,
  brand_guideline: () => null,
};

/** Returns the parser of one asset type. It returns null for a row without an id or of another type.
 *  The brand comes from the path, not the document. */
export function parseBrandAssetOf(brand: string, assetType: BrandAssetType) {
  return (id: string, raw: Record<string, unknown>): BrandAsset | null => {
    if (!id || raw.asset_type !== assetType) return null;
    return {
      id,
      brand,
      assetType,
      name: readString(raw.name),
      fileObject: readString(raw.file_object),
      kind: KIND_OF[assetType](raw.kind),
      playbackObject: readNullableString(raw.playback_object),
      proxyObject: readNullableString(raw.proxy_object),
      transcodeStatus: readNullableOneOf(TRANSCODE_STATUSES, raw.transcode_status),
      isAiCreated: raw.is_ai_created === true,
      waiverGivenBy: readNullableString(readRecord(raw.waiver).given_by),
      tags: readStringList(raw.tags),
      createdAt: readString(readRecord(raw.metadata).created_at),
    };
  };
}

/** Returns the asset in a write response, or null when it has no id or no known type. */
export function parseBrandAssetResponse(response: unknown): BrandAsset | null {
  const raw = readRecord(response);
  const assetId = readString(raw.asset_id);
  const assetType = readNullableOneOf(BRAND_ASSET_TYPES, raw.asset_type);
  return assetId && assetType ? parseBrandAssetOf(readString(raw.brand), assetType)(assetId, raw) : null;
}

/** Returns the earliest-created asset of the list. */
export function firstCreated<T extends { createdAt: string }>(assets: readonly T[]): T | undefined {
  return [...assets].sort((left, right) => left.createdAt.localeCompare(right.createdAt))[0];
}

/** Returns the object to play: the transcoded rendition when there is one, else the upload. */
export function playableObjectOf(asset: BrandAsset): string {
  return asset.playbackObject ?? asset.fileObject;
}
